import { getAppModuleCommands, GoToCategory } from 'owa-appmodule-hotkeys';
import type { HotkeyCommand, Hotkeys } from 'owa-hotkeys';
import type { CommandCategory } from 'owa-hotkeys-map';
import loc from 'owa-localize';
import { isEduStudent } from 'owa-bpos-utils';
import {
    CanvasCategoryKey,
    getTimeStreamHotkeyCommands,
    getCalendarBoardHotkeyCommands,
} from 'owa-timestream-hotkeys';
import {
    createEventsHeaderText,
    navigateCalendarHeaderText,
    calendarActionsHeaderText,
    goToHeaderText_0,
    boardCategoryHeaderText,
    openAgendaInMonthViewCommand,
    newEventCommand,
    saveAppointmentCommand,
    sendMeetingCommand,
    discardDraftCommand_0,
    insertHyperlinkCommand_0,
    undoActionCommand,
    retryActionCommand,
    goToTodayCommand,
    goToPreviousTimePeriodCommand,
    goToNextTimePeriodCommand,
    openDayViewCommand,
    openWorkWeekViewCommand,
    openSchoolWeekViewCommand,
    openWeekViewCommand,
    openMonthViewCommand,
    openBoardViewCommand,
    focusSkipLinksCommand,
    openEventCommand,
    closeEventCommand,
    deleteEventCommand,
    printCalendarCommand,
    searchCalendarsCommand,
    showKeyboardShortcutsCommand_0,
} from './calendarModuleHotKeys.locstring.json';

type HotkeyCommandRegistry = { [key: string]: Required<HotkeyCommand> };

const CreateEventsCategoryKey = 'createEvents';
const NavigateCalendarCategoryKey = 'navigateCalendar';
const CalendarActionsCategoryKey = 'calendarActions';

export function getCommandCategories(isBoardContext: boolean): CommandCategory[] {
    const categories = [
        {
            category: NavigateCalendarCategoryKey,
            displayName: loc(navigateCalendarHeaderText),
        },
        {
            category: CalendarActionsCategoryKey,
            displayName: loc(calendarActionsHeaderText),
        },
        {
            category: CreateEventsCategoryKey,
            displayName: loc(createEventsHeaderText),
        },
        {
            category: GoToCategory,
            displayName: loc(goToHeaderText_0),
        },
    ];

    if (isBoardContext) {
        categories.unshift({
            category: CanvasCategoryKey,
            displayName: loc(boardCategoryHeaderText),
        });
    }

    return categories;
}

export function getCommands(): HotkeyCommandRegistry {
    return {
        ...getAppModuleCommands(),
        ...getTimeStreamCommands(),
        newEvent: createEventsHotkey(newEventCommand, 'n'),
        saveAppointment: createEventsHotkey(saveAppointmentCommand, 'ctrl+s'),
        sendMeeting: createEventsHotkey(sendMeetingCommand, ['ctrl+enter', 'alt+s']),
        // this hotkey is display-only
        // the actual keyboard shortcut-handling logic is implemented in various compose forms, and does not reference this definition
        discardDraft: createEventsHotkey(discardDraftCommand_0, 'esc'),
        // this hotkey is display-only
        // the actual keyboard shortcut-handling logic is in RibbonPlugin, and does not reference this definition
        insertHyperlink: createEventsHotkey(insertHyperlinkCommand_0, 'ctrl+k'),
        undoAction: createEventsHotkey(undoActionCommand, 'ctrl+z'),
        retryAction: createEventsHotkey(retryActionCommand, 'ctrl+shift+y'),
        goToToday: navigateCalendarHotkey(goToTodayCommand, ['shift+alt+y', 'home']),
        prevDateRange: navigateCalendarHotkey(goToPreviousTimePeriodCommand, 'shift+left'),
        nextDateRange: navigateCalendarHotkey(goToNextTimePeriodCommand, 'shift+right'),
        dayView: navigateCalendarHotkey(openDayViewCommand, 'shift+alt+1'),
        workWeekView: navigateCalendarHotkey(
            isEduStudent() ? openSchoolWeekViewCommand : openWorkWeekViewCommand,
            'shift+alt+2'
        ),
        weekView: navigateCalendarHotkey(openWeekViewCommand, 'shift+alt+3'),
        monthView: navigateCalendarHotkey(openMonthViewCommand, 'shift+alt+4'),
        agendaMonthView: navigateCalendarHotkey(openAgendaInMonthViewCommand, 'shift+alt+5'),
        boardView: navigateCalendarHotkey(openBoardViewCommand, 'shift+alt+6'),
        // this hotkey is for initial focus on skip links
        // further keyboard shortcut-handling logic (i.e. navigating through skip links) is implemented in SkipLinkControl, and does not reference this definition
        focusSkipLinks: navigateCalendarHotkey(focusSkipLinksCommand, 'ctrl+f6'),
        // this hotkey is display-only
        // the actual keyboard shortcut-handling logic is in OwaCalendarSurfaceEventHandlers, and does not reference this definition
        openEvent: calendarActionsHotkey(openEventCommand, 'enter'),
        // this hotkey is display-only
        // the actual keyboard shortcut-handling logic is implemented in QuickComposeRenderer, and does not reference this definition
        closeEvent: calendarActionsHotkey(closeEventCommand, 'esc'),
        deleteEvent: calendarActionsHotkey(deleteEventCommand, 'del'),
        printCalendar: calendarActionsHotkey(printCalendarCommand, 'ctrl+p'),
        gotoSearch: calendarActionsHotkey(searchCalendarsCommand, {
            hotmail: '/',
            yahoo: 's',
            gmail: '/',
            owa: 'alt+q',
        }),
        keyboardShortcuts: calendarActionsHotkey(showKeyboardShortcutsCommand_0, '?'),
    };
}

type PerProviderHotkeys = Required<Pick<HotkeyCommand, 'hotmail' | 'yahoo' | 'gmail' | 'owa'>>;

function createHotkeyCommand(
    category: string,
    descriptionResourceId: string,
    hotkey: Hotkeys | PerProviderHotkeys
): Required<HotkeyCommand> {
    return {
        category,
        description: loc(descriptionResourceId),
        ...(typeof hotkey === 'object' && !Array.isArray(hotkey)
            ? hotkey
            : {
                  hotmail: hotkey,
                  yahoo: hotkey,
                  gmail: hotkey,
                  owa: hotkey,
              }),
    };
}

const createEventsHotkey = createHotkeyCommand.bind(null, CreateEventsCategoryKey);
const navigateCalendarHotkey = createHotkeyCommand.bind(null, NavigateCalendarCategoryKey);
const calendarActionsHotkey = createHotkeyCommand.bind(null, CalendarActionsCategoryKey);

function getTimeStreamCommands(): HotkeyCommandRegistry {
    return {
        ...getHotkeyCommandRegistryFromCommands(getTimeStreamHotkeyCommands()),
        ...getHotkeyCommandRegistryFromCommands(getCalendarBoardHotkeyCommands()),
    };
}

function getHotkeyCommandRegistryFromCommands(
    commands: Required<HotkeyCommand>[]
): HotkeyCommandRegistry {
    return commands.reduce((result: HotkeyCommandRegistry, command: Required<HotkeyCommand>) => {
        result[command.description] = command;
        return result;
    }, {});
}
